import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, Typography, Box, Button, CircularProgress, Stack, Grid } from '@mui/material';
import { motion } from 'framer-motion';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LaunchIcon from '@mui/icons-material/Launch';
import { supabase } from '../../lib/supabase';
import EditableImage from '../../components/ui/EditableImage';

const ActivityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [activity, setActivity] = useState(null);
  const [relatedActivities, setRelatedActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dateLocale = i18n.language?.startsWith("fr") ? "fr-FR" : "en-US";

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        setLoading(true);
        const { data, error: fetchError } = await supabase
          .from('activities')
          .select(`
            *,
            translations:activities_translations(*)
          `)
          .eq('id', id)
          .single();

        if (fetchError) throw fetchError;

        const currentLang = i18n.language || 'en';
        const translation = data.translations?.find(t => t.language_code === currentLang) || 
                            data.translations?.find(t => t.language_code === 'en') || 
                            data.translations?.[0] || {};
        
        setActivity({
          ...data,
          title: translation.title || '',
          description: translation.description || '',
          tagline: translation.tagline || ''
        });

        const { data: relatedData } = await supabase
          .from('activities')
          .select(`
            *,
            translations:activities_translations(*)
          `)
          .eq('is_published', true)
          .neq('id', id)
          .order('display_order', { ascending: true })
          .limit(3);

        if (relatedData) {
          const currentLang = i18n.language || 'en';
          const localizedRelated = relatedData.map(act => {
            const trans = act.translations?.find(t => t.language_code === currentLang) || 
                          act.translations?.find(t => t.language_code === 'en') || 
                          act.translations?.[0] || {};
            return {
              ...act,
              title: trans.title || '',
              tagline: trans.tagline || ''
            };
          });
          setRelatedActivities(localizedRelated);
        }
      } catch (err) {
        console.error('Error fetching activity detail:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchActivity();
  }, [id, i18n.language]);

  const getTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'conference': return '#0FA3A3';
      case 'training': return '#D4AF37';
      case 'meetup': return '#0FA3A3';
      case 'publication': return '#D4AF37';
      case 'podcast': return '#0FA3A3';
      default: return '#9CA3AF';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#0B0F14' }}>
        <CircularProgress sx={{ color: '#0FA3A3' }} />
      </Box>
    );
  }

  if (error || !activity) {
    return (
      <Box sx={{ py: 20, textAlign: 'center', bgcolor: '#0B0F14', minHeight: '100vh' }}>
        <Typography color="error" variant="h5">{t("activityDetail.errors.notFound")}</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/activities')} sx={{ mt: 3, color: 'white' }}>
          {t("activityDetail.backToList")}
        </Button>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ bgcolor: '#0B0F14', minHeight: '100vh', pb: 10 }}>
      {/* Hero Header */}
      <Box sx={{ position: 'relative', minHeight: { xs: '70vh', md: '60vh' }, overflow: 'hidden' }}>
        <EditableImage 
          src={activity.cover_image || activity.image_url?.[0] || 'https://via.placeholder.com/1200x600'}
          alt={activity.title}
          tableName="activities"
          recordId={activity.id}
          sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
          imgSx={{ filter: 'brightness(0.4)' }}
        />
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'linear-gradient(to bottom, rgba(11,15,20,0.3) 0%, rgba(11,15,20,0.6) 50%, rgba(11,15,20,0.95) 100%)',
          zIndex: 1
        }} />
        <Container maxWidth="lg" sx={{ 
          position: 'relative', 
          zIndex: 2, 
          pt: { xs: 14, md: 16 },
          pb: 6,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          minHeight: { xs: '70vh', md: '60vh' }
        }}>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Button 
              startIcon={<ArrowBackIcon />} 
              onClick={() => navigate('/activities')} 
              sx={{ color: 'white', mb: 3, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
            >
              {t("activityDetail.backToList")}
            </Button>
            <Box sx={{ 
              display: 'inline-block', 
              bgcolor: 'rgba(11, 15, 20, 0.8)', 
              px: 2, py: 0.5, borderRadius: '8px', 
              border: `1px solid ${getTypeColor(activity.type)}`,
              mb: 2
            }}>
              <Typography variant="caption" sx={{ color: getTypeColor(activity.type), fontWeight: 800, textTransform: 'uppercase' }}>
                {t(`activities.types.${activity.type?.toLowerCase()}`, activity.type)}
              </Typography>
            </Box>
            <Typography variant="h1" sx={{ color: 'white', mb: 2, fontFamily: 'Philosopher', fontSize: { xs: '2rem', md: '3.5rem' }, lineHeight: 1.2 }}>
              {activity.title}
            </Typography>
            {activity.tagline && (
              <Typography variant="h6" sx={{ color: '#D4AF37', fontWeight: 300, mb: 3, maxWidth: '800px', fontSize: { xs: '1rem', md: '1.25rem' } }}>
                {activity.tagline}
              </Typography>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarTodayIcon sx={{ color: '#9CA3AF', fontSize: '1.2rem' }} />
                <Typography sx={{ color: '#9CA3AF' }}>
                  {new Date(activity.date).toLocaleDateString(dateLocale, { day: 'numeric', month: 'long', year: 'numeric' })}
                </Typography>
              </Box>
              {activity.location && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOnIcon sx={{ color: '#9CA3AF', fontSize: '1.2rem' }} />
                  <Typography sx={{ color: '#9CA3AF' }}>{activity.location}</Typography>
                </Box>
              )}
            </Stack>
          </motion.div>
        </Container>
      </Box>

      {/* Content Section */}
      <Container maxWidth="lg" sx={{ mt: -10, position: 'relative', zIndex: 3 }}>
        <Grid container spacing={6}>
          <Grid item xs={12} md={8}>
            <Box sx={{ bgcolor: '#1F2937', p: { xs: 4, md: 8 }, borderRadius: '32px', border: '1px solid rgba(255,255,255,0.05)' }}>
              <Typography variant="h3" sx={{ color: 'white', mb: 4, fontFamily: 'Philosopher' }}>
                {t("activityDetail.sections.overview")}
              </Typography>
              <Typography variant="body1" sx={{ color: '#9CA3AF', fontSize: '1.1rem', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                {activity.description}
              </Typography>

              {activity.image_url && activity.image_url.length > 1 && (
                <Box sx={{ mt: 8 }}>
                  <Typography variant="h4" sx={{ color: 'white', mb: 4, fontFamily: 'Philosopher' }}>
                    {t("activityDetail.sections.gallery")}
                  </Typography>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(6, 1fr)' },
                    gridAutoRows: '150px',
                    gap: 2 
                  }}>
                    {activity.image_url.map((img, idx) => {
                      // Deterministic layout based on index and activity ID
                      const seed = (parseInt(activity.id.slice(0, 8), 16) + idx) % 5;
                      let gridSpan = { gridColumn: 'span 2', gridRow: 'span 2' };
                      
                      if (seed === 0) gridSpan = { gridColumn: { md: 'span 4' }, gridRow: 'span 3' };
                      else if (seed === 1) gridSpan = { gridColumn: { md: 'span 2' }, gridRow: 'span 3' };
                      else if (seed === 2) gridSpan = { gridColumn: { md: 'span 3' }, gridRow: 'span 2' };
                      else if (seed === 3) gridSpan = { gridColumn: { md: 'span 3' }, gridRow: 'span 2' };
                      
                      return (
                        <Box key={idx} sx={{ ...gridSpan }}>
                          <EditableImage 
                            src={img} 
                            alt={`${activity.title} - Image ${idx + 1}`}
                            tableName="activities"
                            recordId={activity.id}
                            sx={{ width: '100%', height: '100%', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)', overflow: 'hidden' }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ position: 'sticky', top: 100 }}>
              <Box sx={{ bgcolor: '#1F2937', p: 4, borderRadius: '24px', border: '1px solid rgba(255,255,255,0.05)', mb: 4 }}>
                <Typography variant="h6" sx={{ color: 'white', mb: 3, fontWeight: 700 }}>
                  {t("activityDetail.sidebar.takeAction")}
                </Typography>
                {activity.url && activity.url !== '#' && (
                  <Button 
                    variant="contained" 
                    fullWidth 
                    endIcon={<LaunchIcon />}
                    component="a"
                    href={activity.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                      bgcolor: '#0FA3A3', 
                      py: 2, 
                      borderRadius: '12px', 
                      fontWeight: 800,
                      '&:hover': { bgcolor: '#0C8A8A' } 
                    }}
                  >
                    {t("activityDetail.sidebar.viewResource")}
                  </Button>
                )}
                <Button 
                  variant="outlined" 
                  fullWidth 
                  onClick={() => navigate('/contact')}
                  sx={{ 
                    mt: 2, 
                    py: 2, 
                    borderRadius: '12px', 
                    color: '#D4AF37', 
                    borderColor: '#D4AF37',
                    fontWeight: 700,
                    '&:hover': { borderColor: '#B89730', bgcolor: 'rgba(212, 175, 55, 0.05)' } 
                  }}
                >
                  {t("activityDetail.sidebar.contactAboutThis")}
                </Button>
              </Box>

              {relatedActivities.length > 0 && (
                <Box sx={{ bgcolor: '#1F2937', p: 4, borderRadius: '24px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <Typography variant="h6" sx={{ color: 'white', mb: 3, fontWeight: 700 }}>
                    {t("activityDetail.sidebar.relatedTitle")}
                  </Typography>
                  <Stack spacing={2}>
                    {relatedActivities.map((related) => (
                      <Box 
                        key={related.id}
                        onClick={() => navigate(`/activities/${related.id}`)}
                        sx={{ 
                          display: 'flex',
                          gap: 2,
                          p: 2,
                          borderRadius: '12px',
                          bgcolor: 'rgba(255,255,255,0.02)',
                          border: '1px solid rgba(255,255,255,0.05)',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': { 
                            bgcolor: 'rgba(15, 163, 163, 0.1)',
                            borderColor: '#0FA3A3'
                          }
                        }}
                      >
                        <Box 
                          component="img"
                          src={related.cover_image || related.image_url?.[0] || 'https://via.placeholder.com/80'}
                          sx={{ 
                            width: 60, 
                            height: 60, 
                            borderRadius: '8px', 
                            objectFit: 'cover',
                            flexShrink: 0
                          }}
                        />
                        <Box sx={{ overflow: 'hidden' }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: getTypeColor(related.type), 
                              fontWeight: 700, 
                              textTransform: 'uppercase',
                              fontSize: '0.65rem'
                            }}
                          >
                            {t(`activities.types.${related.type?.toLowerCase()}`, related.type)}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'white', 
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              lineHeight: 1.3
                            }}
                          >
                            {related.title}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ActivityDetail;
